import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Search, FileSpreadsheet, Calendar, Filter, AlertCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";

import { getStatementReport } from "../../services/statement";
import StatementFilterModal from "./components/StatementFilterModal";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable";
import { useTranslation } from "react-i18next";

const StatementReport = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 6,
    search: "",
    startDate: null,
    endDate: null
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["statements", filter],
    queryFn: () => getStatementReport(filter),
    enabled: !!filter.startDate && !!filter.endDate,
    keepPreviousData: true
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AFN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(value || 0));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      key: "createdAt",
      label: t("date"),
      render: (item) => formatDate(item.createdAt)
    },
    { 
      key: "transactionId", 
      label: t("transactionID") 
    },
    { 
      key: "transactionType", 
      label: t("type"),
      render: (item) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.transactionType?.toLowerCase().includes('credit') 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : item.transactionType?.toLowerCase().includes('debit')
            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
        }`}>
          {item.transactionType}
        </span>
      )
    },
    {
      key: "debit",
      label: t("debit"),
      render: (item) => item.debit ? formatCurrency(item.debit) : "-",
      cellClassName: "text-red-600 dark:text-red-400 font-medium"
    },
    {
      key: "credit",
      label: t("credit"),
      render: (item) => item.credit ? formatCurrency(item.credit) : "-",
      cellClassName: "text-green-600 dark:text-green-400 font-medium"
    },
    {
      key: "walletBalance",
      label: t("balance"),
      render: (item) => formatCurrency(item.walletBalance),
      cellClassName: "font-semibold"
    },
    {
      key: "remarks",
      label: t("remarks"),
      render: (item) => item.remarks || "-"
    },
    {
      key: "agent",
      label: t("agent"),
      render: (item) => (
        <div className="flex items-center gap-2">
          <span>{item.agent?.username || "N/A"}</span>
        </div>
      )
    }
  ];

  const exportData = useMemo(() => {
    if (!data || !data.data) return [];
    
    return data.data.map(item => ({
      "Date": formatDate(item.createdAt),
      "Transaction ID": item.transactionId,
      "Type": item.transactionType,
      "Debit (AFN)": item.debit ? parseFloat(item.debit).toFixed(2) : "-",
      "Credit (AFN)": item.credit ? parseFloat(item.credit).toFixed(2) : "-",
      "Balance (AFN)": parseFloat(item.walletBalance).toFixed(2),
      "Remarks": item.remarks || "-",
      "Agent": item.agent?.username || "N/A",
      "Status": "Completed"
    }));
  }, [data]);

  const handleFilterSubmit = (dateFilter) => {
    setFilter(prev => ({
      ...prev,
      ...dateFilter,
      page: 1
    }));
    setIsFilterModalOpen(false);
    toast.success(t("filterApplied"));
  };

  const handleSearchChange = (value) => {
    setFilter(prev => ({ ...prev, search: value, page: 1 }));
  };

  const generateExcelWithFormatting = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    
    const colWidths = [
      { wch: 20 }, 
      { wch: 25 }, 
      { wch: 15 }, 
      { wch: 15 }, 
      { wch: 15 }, 
      { wch: 15 }, 
      { wch: 30 }, 
      { wch: 15 }, 
      { wch: 10 }, 
    ];
    ws['!cols'] = colWidths;

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        const fillColor = R % 2 === 0 ? "E59213" : "CD0202";
        
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: fillColor } },
          alignment: { 
            horizontal: C >= 3 && C <= 5 ? "right" : "left",
            vertical: "center" 
          },
          border: {
            top: { style: "thin", color: { rgb: "DDDDDD" } },
            bottom: { style: "thin", color: { rgb: "DDDDDD" } },
            left: { style: "thin", color: { rgb: "DDDDDD" } },
            right: { style: "thin", color: { rgb: "DDDDDD" } }
          }
        };

        if (C === 3 && ws[cellAddress].v !== "-") { 
          ws[cellAddress].s.font = { color: { rgb: "FF0000" } };
        }
        if (C === 4 && ws[cellAddress].v !== "-") { 
          ws[cellAddress].s.font = { color: { rgb: "008000" } };
        }
      }
    }

    return ws;
  };

  const handleExport = async () => {
    if (exportData.length === 0) {
      toast.error(t("noDataToExport"));
      return;
    }

    setIsExporting(true);
    
    try {
      const loadingToast = toast.loading(t("preparingExport"));

      const start = filter.startDate 
        ? new Date(filter.startDate).toISOString().split('T')[0] 
        : "start";
      const end = filter.endDate 
        ? new Date(filter.endDate).toISOString().split('T')[0] 
        : "end";
      const fileName = `statement-report-${start}-to-${end}.xlsx`;

      const ws = generateExcelWithFormatting(exportData, fileName);
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Statement Report");
      const summaryData = [
        ["Summary Report"],
        [""],
        ["Report Period", `${start} to ${end}`],
        ["Total Records", exportData.length],
        ["Generated On", new Date().toLocaleString()],
        ["Generated By", "System"],
        [""],
        ["Total Debit", exportData.reduce((sum, row) => {
          const val = parseFloat(row["Debit (AFN)"]);
          return sum + (isNaN(val) ? 0 : val);
        }, 0).toFixed(2)],
        ["Total Credit", exportData.reduce((sum, row) => {
          const val = parseFloat(row["Credit (AFN)"]);
          return sum + (isNaN(val) ? 0 : val);
        }, 0).toFixed(2)],
        ["Net Balance", exportData.reduce((sum, row) => {
          const debit = parseFloat(row["Debit (AFN)"]) || 0;
          const credit = parseFloat(row["Credit (AFN)"]) || 0;
          return sum + (credit - debit);
        }, 0).toFixed(2)]
      ];

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { 
        bookType: "xlsx", 
        type: "array" 
      });
      
      const blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });

      // Save file
      saveAs(blob, fileName);

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(t("exportSuccessful"), {
        icon: '📊',
        duration: 3000
      });

    } catch (error) {
      console.error("Export failed:", error);
      toast.error(t("exportFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const customEmptyState = (
    <div className="w-full min-h-[400px] flex flex-col items-center justify-center p-8">
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl w-24 h-24 flex items-center justify-center mb-6">
        <FileSpreadsheet className="w-12 h-12 text-gray-500 dark:text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {t("noStatementsYet")}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mb-6">
        {t("noStatementsDescription")}
      </p>
      <button
        onClick={() => setIsFilterModalOpen(true)}
        className="px-6 py-2 bg-gradient-to-r from-[#E59213] to-primary text-white rounded-lg  transition-all duration-200 flex items-center gap-2"
      >
        <Calendar size={18} />
        {t("generateStatement")}
      </button>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[400px] flex flex-col items-center justify-center p-8">
      <div className="bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl w-24 h-24 flex items-center justify-center mb-6">
        <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {t("failedToLoad")}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mb-6">
        {error?.message || t("failedTLS")}
      </p>
      <button
        onClick={refetch}
        className="px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center gap-2"
      >
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {t("retry")}
      </button>
    </div>
  );

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-gradient-to-br from-[#EFF2FF] via-[#FAF5FF] to-[#FCF3FB] dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {isFilterModalOpen && (
        <StatementFilterModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onSubmit={handleFilterSubmit}
          isLoading={isLoading}
        />
      )}
      
      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-5">
        <div className="flex items-center w-full md:w-[50%]">
          <div className="max-w-md w-full relative">
            <div className="relative group">
              <input
                onChange={(e) => handleSearchChange(e.target.value)}
                type="text"
                placeholder={t("searchTransactions")}
                className="w-full h-12 pl-12 pr-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 dark:text-white dark:placeholder-gray-400"
                value={filter.search}
              />
              <Search size={20} className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="px-6 py-2.5 bg-gradient-to-r from-[#E59213] to-primary text-white rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-blue-600/20"
          >
            <Filter size={18} />
            <span className="hidden sm:inline">{t("filter")}</span>
          </button>
          
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center gap-2 shadow-lg shadow-red-600/20"
          >
            <Calendar size={18} />
            <span className="hidden sm:inline">{t("generateStatement")}</span>
          </button>
          
          <button
            onClick={handleExport}
            disabled={isExporting || isLoading || !data || data.data.length === 0}
            className={`px-6 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-200 shadow-lg ${
              !isExporting && !isLoading && data && data.data.length > 0
                ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-green-600/20"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="hidden sm:inline">{t("exporting")}</span>
              </>
            ) : (
              <>
                <Download size={18} />
                <span className="hidden sm:inline">{t("export")}</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="mt-6">
        {filter.startDate && filter.endDate && (
          <div className="mb-4 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("showingS")}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {new Date(filter.startDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })} 
                    {" — "}
                    {new Date(filter.endDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              {data?.meta?.total > 0 && (
                <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    {data.meta.total} {t("recordsFound")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <ARHCustomDataTable
            data={data?.data || []}
            columns={columns}
            meta={data?.meta || {
              page: filter.page,
              pages: 1,
              total: 0,
              limit: filter.limit
            }}
            loading={isLoading}
            isError={isError}
            error={error}
            onRetry={refetch}
            onPageChange={(page) => setFilter(prev => ({ ...prev, page }))}
            onLimitChange={(limit) => 
              setFilter(prev => ({ ...prev, limit, page: 1 }))
            }
            searchQuery={filter.search}
            emptyState={customEmptyState}
            errorState={customErrorState}
            className="border-0"
            rowClassName="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
            onRowClick={(item) => console.log('Row clicked:', item)}
          />
        </div>
      </div>
    </div>
  );
};

export default StatementReport;